import { useEffect, useState } from "react";
import { getOfferings, getEventTreatments, getEventLanes } from "@/lib/slotting/api";

export type ScopeItem = { id: string; label: string };

export type RoleTemplateScopeSources = {
  offerings: ScopeItem[];
  treatments: ScopeItem[];
  lanes: ScopeItem[];
  loaded: boolean;
};

/**
 * Loads the option lists for a role template's slotting visibility allowlists:
 * every offering, treatment and lane of the event. Best-effort — a failing
 * source degrades to an empty list rather than breaking the editor. The ids are
 * the ObjectId hex strings stored in the template's `visibleOfferingIds` /
 * `visibleTreatmentIds` / `visibleLaneIds` and matched server-side by
 * `SlottingScopeEnforcer`. Mirrors `useDeviceScopeSources` but loads lanes
 * (collaborator roles scope the slotting board, not seating rooms).
 */
export function useRoleTemplateScopeSources(
  eventId: string | undefined,
): RoleTemplateScopeSources {
  const [offerings, setOfferings] = useState<ScopeItem[]>([]);
  const [treatments, setTreatments] = useState<ScopeItem[]>([]);
  const [lanes, setLanes] = useState<ScopeItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoaded(false);
    (async () => {
      // bypassPreview=true: the pickers must show the FULL catalogue so an admin
      // can WIDEN a role's restriction. Riding the preview bearer would filter
      // these to the role's already-visible subset (can only narrow).
      const [off, trt, lns] = await Promise.all([
        getOfferings(eventId, true).catch(() => []),
        getEventTreatments(eventId, true).catch(() => []),
        getEventLanes(eventId, true).catch(() => []),
      ]);
      if (cancelled) return;
      setOfferings(off.map((o) => ({ id: o.offeringId, label: o.name })));
      setTreatments(trt.map((tr) => ({ id: tr.treatmentId, label: tr.name })));
      setLanes(lns.map((l) => ({ id: l.laneId, label: l.name })));
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return { offerings, treatments, lanes, loaded };
}
